import { useGetFoldersQuery, useGetWorkspacesQuery } from '@carboncopyco/data';
import { CopyOutlineIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import type { DirectoryEntity } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  InputText,
  Loading,
  Modal,
  TextCaption,
  TextSubHeader,
} from '@carboncopyco/ui';
import { useFolderAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { parseNodeId } from '../BrowseTree';
import { FolderTree } from '../FolderTree';

type CopyAssetModalProps = {
  close: () => void;
  origin: {
    id: string;
    entity: DirectoryEntity;
    workspaceId: string;
  };
  onSubmit: ({
    assetName,
    folderId,
    workspaceId,
    targetType,
  }: {
    assetName: string;
    folderId: string;
    workspaceId: string;
    targetType: DirectoryEntity;
  }) => void;
  isLoading?: boolean;
};

export const CopyAssetModal = ({
  close,
  origin,
  onSubmit,
  isLoading,
}: CopyAssetModalProps) => {
  const { organizationId } = useParams();
  const { trackFolderClickCreateCopyModal } =
    useFolderAnalytics('copy_to_modal');

  const { data: workspaces, isLoading: isLoadingWorkspaces } =
    useGetWorkspacesQuery({
      organizationId,
    });
  const { data: folders, isLoading: isLoadingFolders } = useGetFoldersQuery({
    organizationId,
    includeShared: false,
  });

  const [showNewFolderRow, setShowNewFolderRow] = useBoolean();
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [openItemId, setOpenItemId] = useState<string>();

  const validationSchema = object({
    assetName: string().required(
      'To create a new folder, please fill in a name'
    ),
  });

  const handleSubmit = (values: { assetName: string }) => {
    const workspace = workspaces?.find(
      (ws) => String(ws.id) === parseNodeId(selectedItemId)[3]
    );
    trackFolderClickCreateCopyModal({
      workspaceId: workspace.id,
      folderId: parseNodeId(selectedItemId)[0],
      workspaceName: workspace?.workspaceName,
      workspaceType: workspace?.workspaceType,
    });
    onSubmit({
      assetName: values.assetName,
      folderId: parseNodeId(selectedItemId)[0],
      workspaceId: parseNodeId(selectedItemId)[3],
      targetType: parseNodeId<DirectoryEntity>(selectedItemId)[1],
    });
  };

  return (
    <Modal
      open
      onClose={close}
      title={
        <div className="flex items-center mb-2.5 gap-3">
          <CopyOutlineIcon color={ColorHex.natural600} />
          <TextSubHeader label="Copy to" />
        </div>
      }
    >
      <Formik
        initialValues={{ assetName: '' }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values }) => {
          const isOriginSelected =
            selectedItemId &&
            parseNodeId(selectedItemId)[0] === String(origin.id) &&
            parseNodeId(selectedItemId)[3] === String(origin.workspaceId) &&
            parseNodeId(selectedItemId)[1] === origin.entity;
          return (
            <Form>
              <div className="flex flex-col gap-5 w-[460px]">
                <div className="flex flex-col gap-5">
                  <TextCaption
                    color="text-ds-natural-600"
                    label="A copy will be created in the selected place"
                  />
                  <div className="flex flex-col gap-1.5">
                    <label>
                      <TextCaption
                        color="text-ds-natural-600"
                        label="Asset name"
                      />
                      <Field name="assetName">
                        {({ field }) => (
                          <InputText
                            {...field}
                            fullWidth
                          />
                        )}
                      </Field>
                    </label>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <TextCaption
                    color="text-ds-natural-600"
                    label="Copy to"
                  />
                  <div className="h-80 rounded-md border border-ds-natural-150 p-2 overflow-y-auto">
                    <FolderTree
                      workspaces={workspaces}
                      folders={folders}
                      isLoading={isLoadingWorkspaces || isLoadingFolders}
                      actionOriginLocation={origin}
                      selectedItemId={selectedItemId}
                      onSelectedItemId={(selectedItemId) => {
                        setSelectedItemId(selectedItemId);
                      }}
                      openItemId={openItemId}
                      onOpenItemId={setOpenItemId}
                      showAddNewFolderInput={showNewFolderRow}
                      closeAddNewFolderInput={setShowNewFolderRow.setFalse}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Button
                    secondary
                    link
                    label="New folder"
                    leftIcon={<PlusOutlineIcon />}
                    onClick={() => {
                      if (selectedItemId !== openItemId) {
                        setOpenItemId(selectedItemId);
                      }
                      setShowNewFolderRow.setTrue();
                    }}
                    disabled={
                      !selectedItemId ||
                      parseNodeId(selectedItemId)[1] !== 'workspace' ||
                      showNewFolderRow
                    }
                  />

                  <div className="flex gap-2">
                    <Button
                      secondary
                      label="Cancel"
                      onClick={close}
                    />
                    <Button
                      disabled={!values.assetName || isOriginSelected}
                      label={isLoading ? '' : 'Create new copy'}
                      icon={
                        isLoading && <Loading color="text-ds-accent3-100" />
                      }
                      submit
                    />
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
